import { z } from 'zod';
import { API_BASE_URL } from './base-url.js';

// API client + zod schemas for the platform-wide /screener endpoint and
// the /companies/:entityId Company Overview Panel endpoint. See
// apps/api/src/routes/screener.ts for the server-side definitions.
//
// Identity is `entityId` (UUID) throughout — watchlists, screener_presets,
// list memberships, signals all key on the same identifier regardless of
// which market the row came from.

let authTokenGetter: (() => Promise<string | null>) | null = null;
export function setScreenerAuthTokenGetter(getter: (() => Promise<string | null>) | null): void {
  authTokenGetter = getter;
}

async function request<T>(
  path: string,
  schema: { parse: (data: unknown) => T },
  init?: RequestInit,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const token = authTokenGetter ? await authTokenGetter() : null;
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} at ${path}`);
  }
  const json: unknown = await res.json();
  return schema.parse(json);
}

// --- schemas ----------------------------------------------------------

export const screenerRowSchema = z.object({
  entityId: z.string().uuid(),
  companyNumber: z.string(),
  companyName: z.string().nullable(),
  countryCode: z.string(),
  region: z.string().nullable(),
  status: z.string().nullable(),
  registrationDate: z.string().nullable(),

  subsegment: z.string().nullable(),
  segmentGroup: z.string().nullable(),
  /** Clinical-specialism tokens (blueprint X-factor): dementia, orthodontics, … */
  clinicalSpecialisms: z.array(z.string()).default([]),
  /** Specialty tier (canonical-17 → finer bucket). All NULL until Phase-1 backfill. */
  primarySpecialty: z.string().nullable().default(null),
  /** Coarse detection tier: native | register | name | unresolved. */
  primarySpecialtyGrade: z.string().nullable().default(null),
  /** Fine provenance string (e.g. 'register', 'name_fr'); display/filter only. */
  primarySpecialtyBasis: z.string().nullable().default(null),
  /** Bucket whose coefficient priced revenue (platform-derived at recompute). */
  modelSubsegment: z.string().nullable().default(null),
  specialtyModelled: z.boolean().nullable().default(null),
  turnoverScreener: z.number().nullable(),
  turnoverBasis: z.string().nullable(),
  turnoverBand: z.string().nullable(),
  turnoverConfidenceP: z.string().nullable(),
  netAssets: z.number().nullable(),
  employees: z.number().nullable(),
  ownershipType: z.string().nullable(),
  /** Derived independence axis: Independent | Sponsor / roll-up | Corporate-owned. */
  backing: z.string().nullable().default(null),
  /** Premises tenure (migration 0202): Owns premises | Related-party landlord |
   *  Third-party landlord | null (unknown — never "leased"). GB + FR only. */
  tenure: z.string().nullable().default(null),
  /** Sites where the operator itself holds the title / owns the premises. */
  tenureSitesOwned: z.number().nullable().default(null),
  /** Sites held by a related or third-party landlord. */
  tenureSitesLandlord: z.number().nullable().default(null),
  /** GB: an operator-held Freehold title at one of its sites. */
  ownsFreehold: z.boolean().nullable().default(null),
  /** Resolved roll-up group label (enm.economic_group) — search surfaces
   *  cluster member entities under their group header. */
  economicGroup: z.string().nullable().default(null),
  qualityTier: z.string().nullable(),
  /** Quality trajectory — Deteriorating | Stable | Improving | null (n/a). */
  qualityDirection: z.string().nullable().default(null),
  yearsActive: z.number().nullable(),
  /** Corryk Origination Signal (0-100 within-country percentile). */
  originationSignal: z.number().nullable().default(null),
  originationConfidence: z.enum(['High', 'Moderate', 'Indicative']).nullable().default(null),
  /** Corryk Seller-Readiness (0-100 absolute) + its data-completeness tier. */
  sellerReadiness: z.number().nullable().default(null),
  sellerReadinessConfidence: z.enum(['High', 'Moderate', 'Indicative']).nullable().default(null),
  isQuarantined: z.boolean(),
  quarantineReasons: z.array(z.string()).nullable(),
  /** Belgian natural person (no legal entity) — acquirable only as an asset deal. */
  soleTrader: z.boolean().default(false),
});

export type ScreenerRow = z.infer<typeof screenerRowSchema>;

export const screenerListResponseSchema = z.object({
  rows: z.array(screenerRowSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export type ScreenerListResponse = z.infer<typeof screenerListResponseSchema>;

export const screenerFacetsSchema = z.object({
  country: z.array(z.string()),
  region: z.array(z.string()),
  subsegment: z.array(z.string()),
  backing: z.array(z.string()).default([]),
  tenure: z.array(z.string()).default([]),
  ownershipType: z.array(z.string()),
  qualityTier: z.array(z.string()),
  qualityDirection: z.array(z.string()).default([]),
  /** Clinical-specialism launch vocab as tokens; the web maps token → label. */
  clinicalSpecialism: z.array(z.string()).default([]),
  // Regions grouped by their country — drives the Region filter's grouping and
  // "only show regions for the selected countries" behaviour. Defaulted so an
  // older API response (without the field) still parses.
  regionsByCountry: z.record(z.array(z.string())).default({}),
});

export type ScreenerFacets = z.infer<typeof screenerFacetsSchema>;

export const screenerStatsSchema = z.object({
  total: z.number(),
  quarantined: z.number(),
  /** Count of all GB entities with normalised metrics, regardless of
   *  the €150k scope threshold. Useful for "X of Y in platform scope"
   *  copy in admin / debugging surfaces. */
  totalAll: z.number(),
});

export type ScreenerStats = z.infer<typeof screenerStatsSchema>;

// Company Overview Panel — full record. The shape mirrors the API; UK
// rows additionally carry the uk_company_enrichment block, other markets
// will land their own enrichment blocks per `entity.countryCode`.
export const companyEntitySchema = z.object({
  id: z.string().uuid(),
  countryCode: z.string(),
  registryId: z.string().nullable(),
  legalName: z.string().nullable(),
  legalForm: z.string().nullable(),
  status: z.string().nullable(),
  region: z.string().nullable(),
  city: z.string().nullable(),
  postcode: z.string().nullable(),
  registrationDate: z.string().nullable(),
  dissolutionDate: z.string().nullable(),
});

export const companyMetricsSchema = screenerRowSchema
  .pick({
    subsegment: true,
    segmentGroup: true,
    turnoverScreener: true,
    turnoverBasis: true,
    turnoverBand: true,
    turnoverConfidenceP: true,
    netAssets: true,
    employees: true,
    ownershipType: true,
    qualityTier: true,
    qualityDirection: true,
    yearsActive: true,
  })
  .extend({
    owner75Name: z.string().nullable(),
    owner75Age: z.number().nullable(),
    owner75IsDirector: z.string().nullable(),
    corporateParent: z.string().nullable(),
    qualityPercentile: z.string().nullable(),
    qualityBasis: z.string().nullable(),
    accountsOverdue: z.boolean().nullable(),
    outstandingCharges: z.number().nullable(),
    realEstateBacking: z.string().nullable(),
    originationSignal: z.number().nullable(),
    originationConfidence: z.enum(['High', 'Moderate', 'Indicative']).nullable(),
    originationNote: z.string().nullable(),
    sellerReadiness: z.number().nullable(),
    sellerReadinessConfidence: z.enum(['High', 'Moderate', 'Indicative']).nullable(),
    sellerReadinessNote: z.string().nullable(),
  });

export const ukEnrichmentSchema = z
  .object({
    companyNumber: z.string(),
    accountsSizeClass: z.string().nullable(),
    accountsMadeUpTo: z.string().nullable(),
    primarySic: z.string().nullable(),
    primarySicDescription: z.string().nullable(),
    allSicCodes: z.string().nullable(),
    classificationBasis: z.string().nullable(),
    secondarySubsegments: z.string().nullable(),
    turnoverReported: z.number().nullable(),
    profitLoss: z.number().nullable(),
    totalAssets: z.number().nullable(),
    fixedAssets: z.number().nullable(),
    currentAssets: z.number().nullable(),
    cash: z.number().nullable(),
    cqcRegulated: z.boolean().nullable(),
    cqcLocations: z.number().nullable(),
    cqcCarehomeBeds: z.number().nullable(),
    cqcOverallRating: z.string().nullable(),
    cqcPrimaryService: z.string().nullable(),
    cqcBrand: z.string().nullable(),
    rqiaRegulated: z.boolean().nullable(),
    rqiaServices: z.number().nullable(),
    rqiaApprovedPlaces: z.number().nullable(),
    cisRegulated: z.boolean().nullable(),
    cisServices: z.number().nullable(),
    cisRegisteredPlaces: z.number().nullable(),
    cisLowestGrade: z.number().nullable(),
    gphcRegulated: z.boolean().nullable(),
    gphcPharmacies: z.number().nullable(),
    gphcPremisesInspected: z.number().nullable(),
    gphcSitesStandardsNotMet: z.number().nullable(),
    ofstedRegulated: z.boolean().nullable(),
    ofstedSettings: z.number().nullable(),
    ofstedPlaces: z.number().nullable(),
    ofstedWorstGrade: z.string().nullable(),
    ciwRegulated: z.boolean().nullable(),
    ciwServices: z.number().nullable(),
    ciwServiceType: z.string().nullable(),
    ciwLocalAuthority: z.string().nullable(),
    nhsRxItemsAnnual: z.number().nullable(),
    nhsDentalContractValue: z.number().nullable(),
    nhsDentalContracts: z.number().nullable(),
    propertyTitles: z.number().nullable(),
    propertyFreehold: z.number().nullable(),
    propertyLeasehold: z.number().nullable(),
    propertyPricePaidGbp: z.number().nullable(),
    ownsFreehold: z.boolean().nullable(),
    dataQualityNotes: z.string().nullable(),
  })
  .nullable();

export const frEnrichmentSchema = z
  .object({
    siren: z.string().nullable(),
    subsegment: z.string().nullable(),
    revenueSource: z.string().nullable(),
    employeeBand: z.string().nullable(),
    employeeBasis: z.string().nullable(),
    oldestOwnerAge: z.number().nullable(),
    nRepresentants: z.number().nullable(),
    finessEstablishment: z.boolean().nullable(),
    finessCategory: z.string().nullable(),
    finessFacilityCount: z.number().nullable(),
    finessCategories: z.string().nullable(),
    finessBeds: z.number().nullable(),
    hasQualityGrade: z.string().nullable(),
  })
  .nullable();

// Latest filed periods, NATIVE currency (same basis as the metrics above —
// the API selects these straight from entity_financial_periods without the
// EUR FX multiply that /entities/:id/financials applies). Most-recent first.
// Drives Total assets, accounts recency and the Financial-history drill.
export const companyFinancialPeriodSchema = z.object({
  periodEndDate: z.string(),
  revenue: z.number().nullable(),
  totalAssets: z.number().nullable(),
  equity: z.number().nullable(),
  filingDate: z.string().nullable(),
  filingCurrency: z.string().nullable(),
});

// GB property. Who owns the building each site trades from, and on what terms.
// GB-only by construction: HM Land Registry's CCOD is the only bulk corporate
// property register across the 27 markets, so this is null everywhere else
// rather than an empty shape — same convention as ukEnrichment / frEnrichment.
//
// `holderClass` is TIERED and only 'operator' is a fact. 'related_party_psc' is
// a confirmed shared controller; 'related_party_addr' / '_name' are inferences
// from a shared registered postcode or name stem; 'third_party' is a genuine
// external landlord. ⚠️ `pscConfirmed: false` is NOT evidence of independence —
// 41% of unconfirmed edges are a PSC data gap — so the UI must never render an
// unconfirmed holder as independent.
export const ukPropertySiteSchema = z.object({
  siteId: z.string(),
  siteName: z.string().nullable(),
  postcode: z.string().nullable(),
  tenure: z.string().nullable(),
  holderName: z.string().nullable(),
  holderCompanyNumber: z.string().nullable(),
  holderClass: z.string().nullable(),
  pscConfirmed: z.boolean(),
  matchRung: z.string().nullable(),
  pricePaidGbp: z.number().nullable(),
  dateProprietorAdded: z.string().nullable(),
  leaseTerm: z.string().nullable(),
  leaseYears: z.number().nullable(),
  alienationClause: z.boolean().nullable(),
  // Carried over from C'Estimate (2026-10-05). Optional so an older API still parses.
  addressCorroboration: z.string().nullable().optional().default(null),
  ultimateOwner: z.string().nullable().optional().default(null),
  ownerRootKind: z.string().nullable().optional().default(null),
  landlordGroup: z.string().nullable().optional().default(null),
  landlordType: z.string().nullable().optional().default(null),
  leaseYearsLeft: z.number().nullable().optional().default(null),
  leaseExpiry: z.string().nullable().optional().default(null),
});

export const ukPropertySchema = z
  .object({
    sites: z.array(ukPropertySiteSchema),
    summary: z.object({
      titles: z.number(),
      freehold: z.number(),
      leasehold: z.number(),
      operatorHeld: z.number(),
      relatedParty: z.number(),
      thirdParty: z.number(),
      noCorporateTitle: z.number().optional().default(0),
      titlesUnresolved: z.number().optional().default(0),
    }),
  })
  .nullable();

// GB local care labour market at the company's homes (medians; index vs the home's own nation).
export const ukLabourSchema = z
  .object({
    homes: z.number(),
    authorities: z.number(),
    vacancyRate: z.number().nullable(),
    turnoverRate: z.number().nullable(),
    hourlyPayGbp: z.number().nullable(),
    vacancyIndex: z.number().nullable(),
    turnoverIndex: z.number().nullable(),
    payIndex: z.number().nullable(),
    periodLabel: z.string().nullable(),
  })
  .nullable();

export const companyDetailResponseSchema = z.object({
  entity: companyEntitySchema,
  metrics: companyMetricsSchema.nullable(),
  ukEnrichment: ukEnrichmentSchema,
  ukProperty: ukPropertySchema.optional().default(null),
  ukLabour: ukLabourSchema.optional().default(null),
  frEnrichment: frEnrichmentSchema,
  groupSize: z.number(),
  financials: z.array(companyFinancialPeriodSchema),
});

export type CompanyEntity = z.infer<typeof companyEntitySchema>;
export type CompanyMetrics = z.infer<typeof companyMetricsSchema>;
export type UkEnrichment = z.infer<typeof ukEnrichmentSchema>;
export type FrEnrichment = z.infer<typeof frEnrichmentSchema>;
export type CompanyFinancialPeriod = z.infer<typeof companyFinancialPeriodSchema>;
export type UkProperty = z.infer<typeof ukPropertySchema>;
export type UkPropertySite = z.infer<typeof ukPropertySiteSchema>;
export type CompanyDetailResponse = z.infer<typeof companyDetailResponseSchema>;

// --- query types ------------------------------------------------------

export interface ScreenerQuery {
  q?: string;
  country?: readonly string[];
  region?: readonly string[];
  /** Which NUTS grain the region filter/display operates on (migrations
   *  0077-0079). Default 'nuts2' server-side. */
  level?: 'legacy' | 'nuts1' | 'nuts2' | 'nuts3';
  subsegment?: readonly string[];
  /** Canonical entities.sub_segment filter — used by the Market Opportunity
   *  drill-down (its buckets crosswalk from the canonical taxonomy). */
  subSegmentCanonical?: readonly string[];
  /** Role (Alex 2026-10-04): 'operator' (default) lists those who run care; 'landlord' lists the landlords of
   *  in-scope care homes (REITs, propcos, SPVs). A pure landlord never appears in the operator list. */
  role?: 'operator' | 'landlord';
  /** Backing / independence axis: Independent | Sponsor / roll-up | Corporate-owned. */
  backing?: readonly string[];
  /** Premises tenure: Owns premises | Related-party landlord | Third-party landlord | Unknown. */
  tenure?: readonly string[];
  ownershipType?: readonly string[];
  qualityTier?: readonly string[];
  qualityDirection?: readonly string[];
  /** Clinical-specialism tokens — array-overlap match on clinical_specialisms. */
  clinicalSpecialism?: readonly string[];
  /** Revenue sub-subsegment tokens (nursing_home, …) — inArray on primary_specialty. */
  specialty?: readonly string[];
  turnoverMin?: number;
  turnoverMax?: number;
  netAssetsMin?: number;
  netAssetsMax?: number;
  employeesMin?: number;
  employeesMax?: number;
  yearsActiveMin?: number;
  yearsActiveMax?: number;
  revenueConfidenceMin?: number;
  revenueConfidenceMax?: number;
  originationSignalMin?: number;
  originationSignalMax?: number;
  sellerReadinessMin?: number;
  sellerReadinessMax?: number;
  /** Restrict to quarantined rows only. Used by the Data Quality Queue
   *  panel + the screener's quarantine toggle. */
  quarantineOnly?: boolean;
  /** Restrict to independent operators (no corporate parent). Used by the
   *  Market Opportunity drill — the actionable bolt-on pool. */
  independentOnly?: boolean;
  /** Restrict to an explicit set of entity ids — used by the Watchlist
   *  panel to render its members in screener shape. */
  entityIds?: readonly string[];
  sortBy?:
    | 'company_name'
    | 'turnover_screener'
    | 'net_assets'
    | 'employees'
    | 'years_active'
    | 'subsegment'
    | 'region'
    | 'ownership_type'
    | 'quality_tier'
    | 'quality_direction'
    | 'origination_signal'
    | 'seller_readiness';
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

function buildQueryString(q: ScreenerQuery): string {
  const params = new URLSearchParams();
  if (q.q) params.set('q', q.q);
  if (q.country?.length) params.set('country', q.country.join(','));
  if (q.region?.length) params.set('region', q.region.join(','));
  if (q.level) params.set('level', q.level);
  if (q.subsegment?.length) params.set('subsegment', q.subsegment.join(','));
  if (q.subSegmentCanonical?.length)
    params.set('subSegmentCanonical', q.subSegmentCanonical.join(','));
  if (q.role === 'landlord') params.set('role', 'landlord');
  if (q.backing?.length) params.set('backing', q.backing.join(','));
  if (q.tenure?.length) params.set('tenure', q.tenure.join(','));
  if (q.ownershipType?.length) params.set('ownershipType', q.ownershipType.join(','));
  if (q.qualityTier?.length) params.set('qualityTier', q.qualityTier.join(','));
  if (q.qualityDirection?.length) params.set('qualityDirection', q.qualityDirection.join(','));
  if (q.clinicalSpecialism?.length)
    params.set('clinicalSpecialism', q.clinicalSpecialism.join(','));
  if (q.specialty?.length) params.set('specialty', q.specialty.join(','));
  if (q.quarantineOnly) params.set('quarantineOnly', 'true');
  if (q.independentOnly) params.set('independentOnly', 'true');
  if (q.entityIds?.length) params.set('entityIds', q.entityIds.join(','));
  if (q.turnoverMin !== undefined) params.set('turnoverMin', String(q.turnoverMin));
  if (q.turnoverMax !== undefined) params.set('turnoverMax', String(q.turnoverMax));
  if (q.netAssetsMin !== undefined) params.set('netAssetsMin', String(q.netAssetsMin));
  if (q.netAssetsMax !== undefined) params.set('netAssetsMax', String(q.netAssetsMax));
  if (q.employeesMin !== undefined) params.set('employeesMin', String(q.employeesMin));
  if (q.employeesMax !== undefined) params.set('employeesMax', String(q.employeesMax));
  if (q.yearsActiveMin !== undefined) params.set('yearsActiveMin', String(q.yearsActiveMin));
  if (q.yearsActiveMax !== undefined) params.set('yearsActiveMax', String(q.yearsActiveMax));
  if (q.revenueConfidenceMin !== undefined)
    params.set('revenueConfidenceMin', String(q.revenueConfidenceMin));
  if (q.revenueConfidenceMax !== undefined)
    params.set('revenueConfidenceMax', String(q.revenueConfidenceMax));
  if (q.originationSignalMin !== undefined)
    params.set('originationSignalMin', String(q.originationSignalMin));
  if (q.originationSignalMax !== undefined)
    params.set('originationSignalMax', String(q.originationSignalMax));
  if (q.sellerReadinessMin !== undefined)
    params.set('sellerReadinessMin', String(q.sellerReadinessMin));
  if (q.sellerReadinessMax !== undefined)
    params.set('sellerReadinessMax', String(q.sellerReadinessMax));
  if (q.sortBy) params.set('sortBy', q.sortBy);
  if (q.sortDir) params.set('sortDir', q.sortDir);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- public fetchers --------------------------------------------------

export function fetchScreener(q: ScreenerQuery = {}): Promise<ScreenerListResponse> {
  return request(`/screener${buildQueryString(q)}`, screenerListResponseSchema);
}

export function fetchScreenerFacets(
  level?: 'legacy' | 'nuts1' | 'nuts2' | 'nuts3',
): Promise<ScreenerFacets> {
  const qs = level ? `?level=${level}` : '';
  return request(`/screener/facets${qs}`, screenerFacetsSchema);
}

export function fetchScreenerStats(): Promise<ScreenerStats> {
  return request('/screener/stats', screenerStatsSchema);
}

// Fine sub-segment (specialty) coverage — the honesty layer. `capability` is the
// per-(market × specialty) detectability ceiling; `populated` is the set of
// "market:specialty" cells that actually carry data now.
export const specialtyCoverageSchema = z.object({
  capability: z.array(
    z.object({
      market: z.string(),
      specialty: z.string(),
      ceilingGrade: z.string(),
      projectedState: z.string(),
      detectionSource: z.string().nullable(),
    }),
  ),
  populated: z.array(z.string()),
});
export type SpecialtyCoverage = z.infer<typeof specialtyCoverageSchema>;

export function fetchSpecialtyCoverage(): Promise<SpecialtyCoverage> {
  return request('/coverage/specialty', specialtyCoverageSchema);
}

export function fetchCompany(entityId: string): Promise<CompanyDetailResponse> {
  return request(`/companies/${entityId}`, companyDetailResponseSchema);
}
